import { CordovaPlugins } from '../../shared/CordovaPlugins';
import { UserDevice } from '../shared/forms';
import { isAndroid, isWeb, isNative, isIos } from '../../shared/utils/is';
import { LocalNotifications } from '@capacitor/local-notifications';
import { PushNotifications } from '@capacitor/push-notifications';

const DEVICE_ID_KEY = 'push_notifications_device_id';
const LAST_PUSH_ID_KEY = 'last-push-id';
const LAST_PUSH_TIMESTAMP_KEY = 'last_push_notification_registration';

// @todo getSetting / setSetting are temporary until we refactor the entire $f.mobile.settings to use promises
const getSetting = async (key) => {
    return new Promise((resolve, reject) => {
        $f.mobile.settings.get(key, resolve, reject);
    });
};

const setSetting = async (key, value) => {
    return new Promise((resolve, reject) => {
        $f.mobile.settings.set(key, value, resolve, reject);
    });
};

const getDeviceId = async () => {
    return await getSetting(DEVICE_ID_KEY);
};

const setDeviceId = async (id) => {
    await setSetting(DEVICE_ID_KEY, id);
    await setSetting(LAST_PUSH_TIMESTAMP_KEY, new Date());
};

export const getPushDataFromEvent = (event) => event?.notification?.userdata || {};

/** @jira:ZP-3379 @jira:ZP-3376 iOS sends multiple events for the same push notification. We'll block
 * duplicate notifications by checkign for a duplicate timestamp. We can't track this in memory because we
 * sometimes refresh the page when redirecting.
 */
const isDuplicatePush = async (pushId) => {
    const lastId = localStorage.getItem(LAST_PUSH_ID_KEY);
    return lastId === pushId;
};

export const handlePushTapped = async (event) => {
    const pushData = getPushDataFromEvent(event);
    const url = pushData.url;
    const pushId = pushData.push_id;

    if (!url) {
        return;
    }

    if (!isIos() || !pushId) {
        $f.mobile.redirect(url);
        return;
    }

    const isDupe = await isDuplicatePush(pushId);

    if (isDupe) {
        return;
    }

    localStorage.setItem(LAST_PUSH_ID_KEY, pushId);

    $f.mobile.redirect(url);
};

export const handlePushReceived = (event) => {
    const pushData = getPushDataFromEvent(event);
    const url = pushData.url;

    if (!url) {
        return;
    }

    // if notification is related to comments, update the unread count
    if (url.includes('comments')) {
        $f.mobile.unread_notifications_count.fetch();
    }
};

const registerWithZenput = async (token) => {
    const device = new UserDevice({
        id: token,
        name: $f.mobile.settings.get_platform_type('Unknown', true)
    });
    return new Promise((resolve) => {
        device.save(
            {},
            {
                success: async () => {
                    await setDeviceId(token);
                    resolve();
                },
                error: () => {
                    // @author:david nothing we can do - device registration will retry the next time the app is opened
                    resolve();
                }
            }
        );
    });
};

export const addCapacitorPushNotificationsListeners = async () => {
    await PushNotifications.addListener('registration', async (token) => {
        await registerWithZenput(token.value);
    });

    await PushNotifications.addListener('registrationError', (err) => {
        $f.mobile.logging.log_error('Error registering device', { error: err });
    });

    await PushNotifications.addListener('pushNotificationReceived', async (event) => {
        const url = JSON.parse(event.data.u).url;

        // if notification is related to comments, update the unread count
        if (!!url && url.includes('comments')) {
            $f.mobile.unread_notifications_count.fetch();
        }
    });

    await PushNotifications.addListener('pushNotificationActionPerformed', async (event) => {
        const pushData = JSON.parse(event.notification.data.u);
        const url = pushData.url;
        const pushId = pushData.push_id;

        if (!url) {
            return;
        }

        if (!isIos() || !pushId) {
            $f.mobile.redirect(url);
            return;
        }

        const isDupe = await isDuplicatePush(pushId);
        if (isDupe) {
            return;
        }
        localStorage.setItem(LAST_PUSH_ID_KEY, pushId);

        $f.mobile.redirect(url);
    });
};

export const registerWithCapacitor = async () => {
    try {
        const permStatus = await PushNotifications.requestPermissions();
        if (permStatus.receive === 'granted') {
            await PushNotifications.register();
        }
        await LocalNotifications.requestPermissions();
    } catch (error) {
        $f.mobile.logging.log_error('Error with device permissions', { error: error });
    }

    if (isAndroid()) {
        await PushNotifications.createChannel({
            id: 'push_notifications',
            name: 'Push Notifications',
            importance: 4
        });
    }
    await addCapacitorPushNotificationsListeners();
};

export const unregisterDevice = async () => {
    const deviceId = await getDeviceId();
    const device = new UserDevice({ id: deviceId });

    device.destroy(); // hit our server and remove

    await setSetting(DEVICE_ID_KEY, null);
    await setSetting(LAST_PUSH_TIMESTAMP_KEY, null);

    if (isNative()) {
        CordovaPlugins().pushwoosh.unregisterDevice();
    }
};

/**
 * This will initialize our device to receive push notifications. Once we receive
 * a device token (id) from our provider, we send it to our servers for storage.
 */
export const initializePushNotifications = async () => {
    if (isWeb()) {
        return;
    }

    if (!$f.active_user.is_logged_in()) {
        return;
    }

    await registerWithCapacitor();
    await LocalNotifications.addListener(
        'localNotificationActionPerformed',
        $f.mobile.notifications.onLocalNotificationActionPerformed
    );
};
